import { fetchRaw } from "./request";
import { getFilePreviewUrl } from "./attachmentPreview";

export const APP_APK_FILENAME = "gamee.apk";

const isJsonLikeResponse = (response, peekText = "") => {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) return true;
  const trimmed = peekText.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
};

const resolveFetchUrl = url => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) {
    return `${window.location.protocol}${url}`;
  }
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }
  return url;
};

/**
 * @param {Response} response
 * @param {(payload: { loaded: number, total: number, percent: number | null }) => void} [onProgress]
 * @returns {Promise<Blob>}
 */
export async function readResponseAsBlob(response, onProgress) {
  const total = Number(response.headers.get("Content-Length")) || 0;
  const body = response.body;

  if (body && typeof body.getReader === "function") {
    const reader = body.getReader();
    const chunks = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      if (onProgress) {
        const percent =
          total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;
        onProgress({ loaded, total, percent });
      }
    }

    if (onProgress && total <= 0) {
      onProgress({ loaded, total: loaded, percent: 100 });
    }

    return new Blob(chunks);
  }

  const blob = await response.blob();
  if (onProgress) {
    onProgress({
      loaded: blob.size,
      total: blob.size,
      percent: 100,
    });
  }
  return blob;
}

export function saveBlobAsFile(blob, filename = APP_APK_FILENAME) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

/**
 * 通过 CDN / 预览地址 fetch 下载（CDN 已配置 CORS 时可重命名）
 * @param {string} url
 * @param {Object} [options]
 * @returns {Promise<boolean>}
 */
export async function downloadFileFromUrl(url, options = {}) {
  const { filename = APP_APK_FILENAME, onProgress } = options;
  const resolvedUrl = resolveFetchUrl(url);
  if (!resolvedUrl) return false;

  try {
    const response = await fetch(resolvedUrl, {
      mode: "cors",
      credentials: "omit",
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await readResponseAsBlob(response, onProgress);
    if (!blob.size) return false;

    saveBlobAsFile(blob, filename);
    return true;
  } catch (e) {
    console.warn("CDN fetch 下载失败:", e);
    return false;
  }
}

/**
 * 浏览器直连 CDN 下载（不走网关代理，速度最快）
 * 跨域时 download 属性可能无效，文件名取决于 CDN/OSS 响应头
 * @param {string} url
 * @param {string} [filename]
 * @returns {boolean}
 */
export function triggerDirectDownload(url, filename = APP_APK_FILENAME) {
  const resolvedUrl = resolveFetchUrl(url);
  if (!resolvedUrl) return false;

  try {
    const link = document.createElement("a");
    link.href = resolvedUrl;
    link.download = filename;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (e) {
    console.warn("直连 CDN 下载失败:", e);
    return false;
  }
}

/**
 * 通过同源 API 下载附件（较慢，仅作 CDN 不可用时的兜底）
 * @param {string} attachmentUuid
 * @param {Object} [options]
 * @returns {Promise<boolean>}
 */
export async function downloadAttachmentByUuid(attachmentUuid, options = {}) {
  const { filename = APP_APK_FILENAME, onProgress } = options;
  if (!attachmentUuid) return false;

  const attempts = [
    () =>
      fetchRaw("/file/download", {
        method: "POST",
        body: JSON.stringify({ attachmentUuid }),
      }),
    () =>
      fetchRaw(
        `/file/download?attachmentUuid=${encodeURIComponent(attachmentUuid)}`,
        { method: "GET" },
      ),
  ];

  for (const attempt of attempts) {
    try {
      const response = await attempt();
      if (!response.ok) continue;

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      const isBinary =
        contentType.includes("octet-stream") ||
        contentType.includes("application/vnd.android") ||
        contentType.includes("application/zip") ||
        contentType.includes("application/apk");

      if (!isBinary) {
        if (
          contentType.includes("application/json") ||
          contentType.includes("text/plain")
        ) {
          const peekText = await response.clone().text();
          if (isJsonLikeResponse(response, peekText)) {
            continue;
          }
        }
      }

      const blob = await readResponseAsBlob(response, onProgress);
      if (!blob.size) continue;

      saveBlobAsFile(blob, filename);
      return true;
    } catch (e) {
      console.warn("附件同源下载尝试失败:", e);
    }
  }

  return false;
}

/**
 * 下载附件并保存为指定文件名
 * 1. CDN fetch（快，CORS 允许时可重命名为 gamee.apk）
 * 2. CDN 直连（最快，文件名取决于 OSS/CDN 响应头）
 * 3. 同源 API 代理（慢，兜底重命名）
 * @param {string} attachmentUuid
 * @param {Object} [options]
 * @returns {Promise<'ok' | 'renamed' | 'no-url' | 'fallback'>}
 *   - renamed: CDN/API Blob 下载并成功重命名
 *   - ok: 已触发 CDN 直连下载
 */
export async function downloadAttachmentAs(attachmentUuid, options = {}) {
  const { filename = APP_APK_FILENAME, onProgress } = options;

  const previewUrl = await getFilePreviewUrl(attachmentUuid);
  if (!previewUrl) {
    return "no-url";
  }

  // 1. 优先 CDN fetch：走 CDN 边缘节点，CORS 允许时可重命名
  const cdnFetchOk = await downloadFileFromUrl(previewUrl, {
    filename,
    onProgress,
  });
  if (cdnFetchOk) return "renamed";

  // 2. CDN 直连：浏览器直接从 CDN/OSS 拉取，不经过网关
  if (triggerDirectDownload(previewUrl, filename)) {
    return "ok";
  }

  // 3. 兜底：走同源 API（慢，但可重命名）
  const apiOk = await downloadAttachmentByUuid(attachmentUuid, {
    filename,
    onProgress,
  });
  if (apiOk) return "renamed";

  window.open(previewUrl, "_blank", "noopener,noreferrer");
  return "fallback";
}

// 兼容旧引用
export const downloadFileAs = downloadFileFromUrl;
